/**
 * WebSocket client for AI agents (Strategy Orchestrator, Code Explorer, etc.).
 * Handles the structured WebSocketEnvelope format with state machine.
 */

import { getStrategyWebSocketUrl } from '../config/api';

class StrategyWebSocketClient {
    /**
     * Create a WebSocket client for an AI agent.
     * @param {string} customWsUrl - Optional custom WebSocket URL. If not provided, uses getStrategyWebSocketUrl().
     */
    constructor(customWsUrl = null) {
        this.ws = null;
        this.sessionId = null;
        this.token = null;
        this.agentName = null;
        this.customWsUrl = customWsUrl;
        this.messageHandlers = [];
        this.stateHandlers = [];
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = 3000;
        this.lastEventSequence = 0;
    }

    /**
     * Connect to Strategy WebSocket server with JWT token.
     * @param {string} token - JWT token from localStorage
     * @param {string} existingSessionId - Optional session ID to resume
     * @param {string} agentName - Optional agent name to connect to
     */
    connect(token, existingSessionId = null, agentName = null) {
        if (!token) {
            console.error('Token is required for WebSocket connection');
            return Promise.reject(new Error('Token is required'));
        }

        this.token = token;
        this.agentName = agentName;

        return new Promise((resolve, reject) => {
            try {
                const wsUrl = this.customWsUrl || getStrategyWebSocketUrl();
                console.log('[AgentWebSocketClient] Connecting to:', wsUrl);
                this.ws = new WebSocket(wsUrl);

                this.ws.onopen = () => {
                    console.log('[StrategyWebSocketClient] Connected');
                    this.reconnectAttempts = 0;

                    // Send INIT message with token and agentName
                    const initMsg = {
                        type: 'INIT',
                        token: this.token,
                        sessionId: existingSessionId
                    };
                    if (this.agentName) {
                        initMsg.agentName = this.agentName;
                    }
                    this.send(initMsg);
                };

                this.ws.onmessage = (event) => {
                    try {
                        const envelope = JSON.parse(event.data);
                        this.handleEnvelope(envelope, resolve, reject);
                    } catch (error) {
                        console.error('[StrategyWebSocketClient] Error parsing message:', error);
                    }
                };

                this.ws.onerror = (error) => {
                    console.error('[StrategyWebSocketClient] Error:', error);
                    reject(error);
                };

                this.ws.onclose = (event) => {
                    console.log('[StrategyWebSocketClient] Closed:', event.code, event.reason);

                    if (event.code !== 1008 && this.reconnectAttempts < this.maxReconnectAttempts) {
                        this.attemptReconnect(existingSessionId);
                    }
                };
            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * Handle incoming WebSocket envelope.
     * Envelope format: { sessionId, state, subAgent?, payload }
     */
    handleEnvelope(envelope, resolve, reject) {
        console.log('[AgentWebSocketClient] Envelope:', envelope);

        // Handle RECONNECTED response from backend
        if (envelope.type === 'RECONNECTED') {
            this.sessionId = envelope.sessionId;
            console.log(`[StrategyWebSocketClient] Reconnected, ${envelope.missedEvents || 0} events to replay`);
            if (resolve) resolve(this.sessionId);
            return;
        }

        // Handle replayed user messages during reconnection
        if (envelope.type === 'REPLAY_USER_MESSAGE') {
            this.lastEventSequence++;
            this.notifyHandlers({
                type: 'REPLAY_USER_MESSAGE',
                content: envelope.content,
                timestamp: envelope.timestamp
            });
            return;
        }

        // Check for initialization success
        if (envelope.sessionId && !this.sessionId) {
            this.sessionId = envelope.sessionId;
            this.lastEventSequence = 0; // New session, reset sequence
            console.log('[AgentWebSocketClient] Session:', this.sessionId);
            if (resolve) resolve(this.sessionId);
        }

        // Handle PERMISSION_REQUIRED for HITL (CLI commands)
        if (envelope.type === 'PERMISSION_REQUIRED') {
            console.log('[AgentWebSocketClient] Permission required:', envelope.command);
            this.notifyHandlers({
                type: 'PERMISSION_REQUIRED',
                commandId: envelope.commandId,
                command: envelope.command,
                message: envelope.message || `The agent wants to execute: ${envelope.command}`
            });
            return;
        }

        // Check for error
        if (envelope.type === 'ERROR' || envelope.state === 'ERROR') {
            const errorMsg = envelope.content || envelope.payload?.content || 'Unknown error';
            console.error('[AgentWebSocketClient] Error:', errorMsg);
            this.notifyHandlers({
                type: 'ERROR',
                content: errorMsg
            });
            if (reject) reject(new Error(errorMsg));
            return;
        }

        // Track event sequence for reconnection replay
        this.lastEventSequence++;

        // Notify state handlers
        if (envelope.state) {
            this.notifyStateHandlers(envelope.state, envelope.subAgent);
        }

        // Convert envelope to message format for handlers
        const message = this.envelopeToMessage(envelope);
        if (message) {
            this.notifyHandlers(message);
        }
    }

    /**
     * Strip markdown code fences from content.
     * The LLM sometimes wraps JSON in ```json ... ```
     */
    stripCodeFences(text) {
        if (!text || typeof text !== 'string') return text;

        // Match ```json, ```JSON, ```, etc. at start and ``` at end
        const codeFenceRegex = /^```(?:json|JSON)?\s*\n?([\s\S]*?)\n?```$/;
        const match = text.trim().match(codeFenceRegex);
        if (match) {
            return match[1].trim();
        }
        return text;
    }

    /**
     * Convert envelope format to message format compatible with chat UI.
     */
    envelopeToMessage(envelope) {
        let { state, subAgent, payload } = envelope;

        // If payload is a string, it might be wrapped in markdown code fences
        if (typeof payload === 'string') {
            payload = this.stripCodeFences(payload);
        }

        // Sometimes the payload itself is a JSON string containing an envelope
        // Try to parse it if it looks like JSON
        if (typeof payload === 'string' && payload.trim().startsWith('{')) {
            try {
                const parsed = JSON.parse(payload);
                if (parsed.state) {
                    // It's a nested envelope
                    state = parsed.state;
                    subAgent = parsed.subAgent || subAgent;
                    payload = parsed.payload;
                }
            } catch (e) {
                // Not JSON, use as-is
            }
        }

        // Extract content from payload
        let content = '';
        if (typeof payload === 'string') {
            // Strip code fences again in case content is wrapped
            payload = this.stripCodeFences(payload);

            // Check if payload is a JSON string containing envelope
            if (payload.trim().startsWith('{')) {
                try {
                    const parsed = JSON.parse(payload);
                    if (parsed.payload?.content) {
                        content = parsed.payload.content;
                        state = parsed.state || state;
                        subAgent = parsed.subAgent || subAgent;
                    } else if (parsed.content) {
                        content = parsed.content;
                    } else {
                        content = payload;
                    }
                } catch (e) {
                    content = payload;
                }
            } else {
                content = payload;
            }
        } else if (payload?.content) {
            content = payload.content;
        }

        // Also strip code fences from extracted content
        if (typeof content === 'string') {
            content = this.stripCodeFences(content);
        }

        if (!content && state !== 'THINKING' && state !== 'WORKING') {
            return null;
        }

        // Map state to message type
        switch (state) {
            case 'THINKING':
                return {
                    type: 'THINKING',
                    content: content || 'Thinking...',
                    subAgent
                };

            case 'WORKING':
                return {
                    type: 'WORKING',
                    content: content || `${subAgent || 'Agent'} is working...`,
                    subAgent
                };

            case 'WAITING_FOR_USER':
                // WAITING_FOR_USER means agent is done and waiting for user input
                // Mark as finished to reset loading state and enable input
                return {
                    type: 'PARTIAL',
                    content,
                    subAgent,
                    finished: true,  // Allow user to type again
                    waitingForUser: true
                };

            case 'FINAL':
                return {
                    type: 'PARTIAL',
                    content,
                    subAgent,
                    finished: true
                };

            default:
                if (content) {
                    return {
                        type: 'PARTIAL',
                        content,
                        subAgent
                    };
                }
                return null;
        }
    }

    /**
     * Send a message to the strategy agent.
     * @param {string} text - Message text
     */
    sendMessage(text) {
        if (!this.sessionId) {
            console.error('[AgentWebSocketClient] No active session');
            return;
        }

        this.send({
            type: 'MESSAGE',
            text: text
        });
    }

    /**
     * Grant permission for a CLI command (HITL).
     * @param {string} commandId - The command ID to grant permission for
     */
    grantPermission(commandId) {
        if (!this.sessionId) {
            console.error('[AgentWebSocketClient] No active session');
            return;
        }

        this.send({
            type: 'GRANT_PERMISSION',
            commandId: commandId
        });
    }

    /**
     * Deny permission for a CLI command (HITL).
     * @param {string} commandId - The command ID to deny permission for
     */
    denyPermission(commandId) {
        if (!this.sessionId) {
            console.error('[AgentWebSocketClient] No active session');
            return;
        }

        this.send({
            type: 'DENY_PERMISSION',
            commandId: commandId
        });
    }

    /**
     * Refresh the JWT token.
     * @param {string} newToken - New JWT token
     */
    refreshToken(newToken) {
        if (!newToken) {
            console.error('[StrategyWebSocketClient] New token is required');
            return;
        }

        this.token = newToken;
        this.send({
            type: 'REFRESH_TOKEN',
            token: newToken
        });
    }

    /**
     * Attempt to reconnect to WebSocket.
     * Uses RECONNECT message type to trigger event replay on the backend.
     */
    attemptReconnect(existingSessionId) {
        this.reconnectAttempts++;
        const reconnectSessionId = this.sessionId || existingSessionId;
        console.log(`[StrategyWebSocketClient] Reconnecting (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);

        if (!reconnectSessionId) {
            // No session to reconnect to — fall back to fresh INIT
            console.warn('[StrategyWebSocketClient] No sessionId for reconnect, falling back to INIT');
            setTimeout(() => {
                this.connect(this.token, null, this.agentName)
                    .catch(error => {
                        console.error('[StrategyWebSocketClient] Reconnection failed:', error);
                    });
            }, this.reconnectDelay);
            return;
        }

        setTimeout(() => {
            this.reconnect(reconnectSessionId)
                .catch(error => {
                    console.error('[StrategyWebSocketClient] Reconnection failed:', error);
                });
        }, this.reconnectDelay);
    }

    /**
     * Reconnect to an existing session using the RECONNECT message type.
     * This avoids full re-initialization and replays only missed events.
     * @param {string} sessionId - Session ID to reconnect to
     */
    reconnect(sessionId) {
        return new Promise((resolve, reject) => {
            try {
                const wsUrl = this.customWsUrl || getStrategyWebSocketUrl();
                console.log('[StrategyWebSocketClient] Reconnecting to:', wsUrl, 'session:', sessionId);
                this.ws = new WebSocket(wsUrl);

                this.ws.onopen = () => {
                    console.log('[StrategyWebSocketClient] WebSocket open, sending RECONNECT');
                    this.reconnectAttempts = 0;
                    const reconnectMsg = {
                        type: 'RECONNECT',
                        token: this.token,
                        sessionId: sessionId,
                        lastEventSequence: this.lastEventSequence
                    };
                    if (this.agentName) {
                        reconnectMsg.agentName = this.agentName;
                    }
                    this.send(reconnectMsg);
                };

                this.ws.onmessage = (event) => {
                    try {
                        const envelope = JSON.parse(event.data);
                        this.handleEnvelope(envelope, resolve, reject);
                    } catch (error) {
                        console.error('[StrategyWebSocketClient] Error parsing message:', error);
                    }
                };

                this.ws.onerror = (error) => {
                    console.error('[StrategyWebSocketClient] Reconnect error:', error);
                    reject(error);
                };

                this.ws.onclose = (event) => {
                    console.log('[StrategyWebSocketClient] Closed after reconnect:', event.code, event.reason);
                    if (event.code !== 1008 && this.reconnectAttempts < this.maxReconnectAttempts) {
                        this.attemptReconnect(sessionId);
                    }
                };
            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * Register a message handler.
     * @param {Function} handler - Handler function
     */
    onMessage(handler) {
        this.messageHandlers.push(handler);
    }

    /**
     * Register a state change handler.
     * @param {Function} handler - Handler function (state, subAgent)
     */
    onStateChange(handler) {
        this.stateHandlers.push(handler);
    }

    /**
     * Unregister a message handler.
     * @param {Function} handler - Handler function
     */
    offMessage(handler) {
        this.messageHandlers = this.messageHandlers.filter(h => h !== handler);
    }

    /**
     * Notify all message handlers.
     */
    notifyHandlers(message) {
        this.messageHandlers.forEach(handler => {
            try {
                handler(message);
            } catch (error) {
                console.error('[StrategyWebSocketClient] Error in message handler:', error);
            }
        });
    }

    /**
     * Notify all state handlers.
     */
    notifyStateHandlers(state, subAgent) {
        this.stateHandlers.forEach(handler => {
            try {
                handler(state, subAgent);
            } catch (error) {
                console.error('[StrategyWebSocketClient] Error in state handler:', error);
            }
        });
    }

    /**
     * Send a raw message to WebSocket.
     */
    send(message) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(message));
        } else {
            console.error('[StrategyWebSocketClient] Not connected');
        }
    }

    /**
     * Close the WebSocket connection.
     */
    close() {
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        this.sessionId = null;
        // Don't reset lastEventSequence — it's needed for reconnect replay.
        // It only resets on new session creation (in handleEnvelope).
    }

    /**
     * Check if WebSocket is connected.
     */
    isConnected() {
        return this.ws && this.ws.readyState === WebSocket.OPEN;
    }
}

export default StrategyWebSocketClient;
